import React, { useMemo, useState } from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { FilterBanner } from '../../common/cardStats/FilterBanner';
import { formatNumber, formatPct } from '../../common/cardStats/formatters';
import { SALT_CATEGORY_SHORT_LABELS } from '../../../utils/SaltCategoryLabels';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';

/**
 * Canonical histogram bucket order from the API contract — keep in sync
 * with the cutoffs in §3 of api-change-commandercardstats-salt.md.
 *
 * `bounds` drive the needle-position math on the pulse gauge: each
 * bucket has equal on-screen width (20%) but represents a different
 * numeric range. The final bucket is open-ended but we cap at a virtual
 * upper for linear interpolation inside the 60+ band so needles past
 * ~120 saturate at the right edge instead of flying off.
 */
const HISTOGRAM_BUCKETS = [
    { key: 'salt_0_5', label: '0–5', range: '0 ≤ total < 5', bounds: [0, 5] },
    { key: 'salt_5_15', label: '5–15', range: '5 ≤ total < 15', bounds: [5, 15] },
    { key: 'salt_15_30', label: '15–30', range: '15 ≤ total < 30', bounds: [15, 30] },
    { key: 'salt_30_60', label: '30–60', range: '30 ≤ total < 60', bounds: [30, 60] },
    { key: 'salt_60_plus', label: '60+', range: '60 ≤ total', bounds: [60, 120] },
];

/**
 * Column / gauge-zone colors for the pulse banner. Left-to-right:
 * mild-green → yellow-green → yellow → orange → seared red. Visually
 * communicates "more right = more salt = hotter" without the viewer
 * needing a legend.
 */
const PULSE_COLORS = {
    salt_0_5: '#5fa85a',
    salt_5_15: '#a8c03a',
    salt_15_30: '#e8c600',
    salt_30_60: '#e08a30',
    salt_60_plus: '#cc1a1a',
};

const INTENSITY_ORDER = ['mild', 'moderate', 'high', 'extreme'];
const INTENSITY_COLOR = {
    mild: '#5fa85a',
    moderate: '#e8c600',
    high: '#e08a30',
    extreme: '#cc1a1a',
};
const INTENSITY_LABEL = {
    mild: 'Mild',
    moderate: 'Moderate',
    high: 'High',
    extreme: 'Extreme',
};

/**
 * Verbs the hero card uses to describe each intensity tier. Plugs into
 * the one-line blurb so a `dominantIntensity` of `high` reads as
 * "leans hard" rather than "high".
 */
const INTENSITY_VERB = {
    mild: 'stays light',
    moderate: 'plays it moderate',
    high: 'leans hard',
    extreme: 'plays brutal',
};

/**
 * Friendly labels for the archetype families emitted by
 * SaltPersonalityCatalog. Falls back to `humanize` for any new keys
 * server-side adds without a client redeploy.
 */
const FAMILY_LABEL = {
    land_denial: 'Land denial',
    stax: 'Stax',
    boardwipe: 'Boardwipe',
    combo: 'Combo',
    wallet: 'Wallet',
    hand_hate: 'Hand hate',
    combat: 'Combat',
    theft: 'Theft',
    annihilator: 'Annihilator',
    pain: 'Pain',
    chaos: 'Chaos',
    poison: 'Poison',
    sacrifice: 'Sacrifice',
    tax_cost: 'Tax cost',
    meta: 'Meta density',
    mixed: 'Mixed',
};

const CATEGORY_COLOR = {
    mld: '#8a5a2a',
    boardwipes: '#d6534a',
    counters: '#4a9bd6',
    stax: '#8a7a9e',
    taxes: '#8a7a9e',
    annihilator: '#cc1a1a',
    cedhMetashare: '#e8c600',
    sacrifice: '#6a3a6a',
    reserved: '#d4a03a',
    discard: '#6a5a8a',
    cantCast: '#4a9bd6',
    cantEven: '#4a9bd6',
    cantUntap: '#4a9bd6',
    cantActivate: '#4a9bd6',
    cantSearch: '#4a9bd6',
    cantDraw: '#4a9bd6',
    cantAttack: '#4a9bd6',
    cantTrigger: '#4a9bd6',
    cantGainLife: '#4a9bd6',
    payTheOne: '#8a5a2a',
    infiniteCombos: '#cc1a1a',
    edhrec: '#5fa85a',
    locks: '#8a7a9e',
    theft: '#6a5a8a',
    chaos: '#d6534a',
    groupSlug: '#d6534a',
    extraTurns: '#e8c600',
    extraCombats: '#e8c600',
    poison: '#5fa85a',
    wheels: '#6a5a8a',
    offColorFetches: '#d4a03a',
    cardPrice: '#d4a03a',
};

const DEFAULT_CATEGORY_COLOR = '#e8c600';

const categoryColor = (key) => CATEGORY_COLOR[key] || DEFAULT_CATEGORY_COLOR;

const humanize = (key) => {
    if (!key) return '';
    return String(key)
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const prettyCategory = (key) => SALT_CATEGORY_SHORT_LABELS[key] || humanize(key);

const prettyFamily = (key) => FAMILY_LABEL[key] || humanize(key);

/**
 * Map an avgTotal value onto the pulse-gauge x-axis [0..100]. Each
 * bucket owns 20% of the track; position inside a bucket is linear
 * between its bounds. Needle saturates at 100% for anything beyond the
 * last virtual cap (120) so high-variance commanders still render.
 */
const gaugePosition = (value) => {
    if (!value || value <= 0) return 0;
    for (let i = 0; i < HISTOGRAM_BUCKETS.length; i++) {
        const [lo, hi] = HISTOGRAM_BUCKETS[i].bounds;
        if (value < hi || i === HISTOGRAM_BUCKETS.length - 1) {
            const frac = Math.min(Math.max((value - lo) / (hi - lo), 0), 1);
            return ((i + frac) / HISTOGRAM_BUCKETS.length) * 100;
        }
    }
    return 100;
};

const toCardRowShape = (card) => ({
    ...card,
    types: Array.isArray(card?.types) ? card.types.join(' ') : card?.types,
    count: card?.deckCount,
    inclusionRate: card?.deckRate,
});

/**
 * Salt pulse banner — one visual that fuses the 5-tile stat grid with
 * the histogram distribution. Columns over a colored gauge show where
 * the bucket mass sits; two needles mark the overall avg and the avg
 * among decks that actually scored salt.
 */
function SaltPulseBanner({ stats, deckCount }) {
    const avg = stats.avgTotal || 0;
    const avgSalted = stats.avgTotalAmongSalted || 0;
    const bucketRates = HISTOGRAM_BUCKETS.map((b) => stats.totalHistogram?.[b.key]?.rate || 0);
    const maxRate = Math.max(...bucketRates, 0.01);
    const avgPos = gaugePosition(avg);
    const avgSaltedPos = gaugePosition(avgSalted);
    // Hide the second needle when avg and avgSalted overlap (no salt-free
    // decks in the bin — the two numbers are identical) to avoid a double
    // marker right on top of itself.
    const showSaltedNeedle = Math.abs(avgPos - avgSaltedPos) > 2;

    return (
        <div className="saltPulseCard">
            <div className="saltPulseHeader">
                <span className="saltPulseEyebrow">Salt pulse</span>
                <span className="saltPulseSaltyShare">
                    {`${stats.decksWithSalt || 0} / ${deckCount} decks score salt · ${formatPct(
                        stats.decksWithSaltRate
                    )}`}
                </span>
            </div>

            <div className="saltPulseChart">
                <div className="saltPulseColumns">
                    {HISTOGRAM_BUCKETS.map((bucket, idx) => {
                        const rate = bucketRates[idx];
                        const entry = stats.totalHistogram?.[bucket.key] || {};
                        const heightPct = maxRate > 0 ? (rate / maxRate) * 100 : 0;
                        return (
                            <div
                                key={bucket.key}
                                className="saltPulseColumn"
                                title={`${entry.count || 0} decks with total salt ${bucket.range} — ${formatPct(
                                    rate
                                )} of this bucket`}
                            >
                                <span className="saltPulseColumnCount">{entry.count || 0}</span>
                                <div className="saltPulseColumnTrack">
                                    <div
                                        className="saltPulseColumnFill"
                                        style={{
                                            height: `${Math.max(heightPct, 2)}%`,
                                            background: PULSE_COLORS[bucket.key],
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="saltPulseGauge">
                    {HISTOGRAM_BUCKETS.map((bucket) => (
                        <div
                            key={bucket.key}
                            className="saltPulseGaugeZone"
                            style={{ background: PULSE_COLORS[bucket.key] }}
                        />
                    ))}
                    {avg > 0 && (
                        <div
                            className="saltPulseNeedle saltPulseNeedle--primary"
                            style={{ left: `${avgPos}%` }}
                            title={`Avg total salt per deck: ${formatNumber(avg, 1)}`}
                        >
                            <div className="saltPulseNeedleMarker" />
                            <span className="saltPulseNeedleLabel">{`avg ${formatNumber(avg, 1)}`}</span>
                        </div>
                    )}
                    {showSaltedNeedle && avgSalted > 0 && (
                        <div
                            className="saltPulseNeedle saltPulseNeedle--secondary"
                            style={{ left: `${avgSaltedPos}%` }}
                            title={`Avg among decks that scored salt: ${formatNumber(avgSalted, 1)}`}
                        >
                            <div className="saltPulseNeedleMarker saltPulseNeedleMarker--secondary" />
                            <span className="saltPulseNeedleLabel saltPulseNeedleLabel--secondary">
                                {`when salty ${formatNumber(avgSalted, 1)}`}
                            </span>
                        </div>
                    )}
                </div>

                <div className="saltPulseAxis">
                    {HISTOGRAM_BUCKETS.map((bucket) => (
                        <span key={bucket.key} className="saltPulseAxisLabel">
                            {bucket.label}
                        </span>
                    ))}
                </div>
            </div>

            <div className="saltPulseSatellites">
                <SatelliteStat label="Max" value={formatNumber(stats.maxTotal, 1)} hint="hottest single deck" />
                <SatelliteStat
                    label="σ spread"
                    value={formatNumber(stats.stddevTotal, 1)}
                    hint="how varied the lists are"
                />
                <SatelliteStat
                    label="Top category avg"
                    value={formatNumber(stats.avgMaxCategoryScore, 1)}
                    hint="per-deck dominant lane"
                />
            </div>
        </div>
    );
}

function SatelliteStat({ label, value, hint }) {
    return (
        <div className="saltPulseSatellite" title={hint}>
            <span className="saltPulseSatelliteLabel">{label}</span>
            <span className="saltPulseSatelliteValue">{value}</span>
            <span className="saltPulseSatelliteHint">{hint}</span>
        </div>
    );
}

/**
 * Salt personality hero — gradient card keyed to dominantIntensity,
 * big family label, one-line blurb, and the classifier chip rows as
 * a footer. Replaces the flat "personality" section header so the
 * commander's salt identity reads at a glance.
 */
function SaltPersonalityHero({ personality, deckCount }) {
    const intensityShare = personality?.intensityShare || {};
    const dominantIntensity = personality?.dominantIntensity;
    const topFamily = personality?.primaryFamilies?.[0];
    const accent = INTENSITY_COLOR[dominantIntensity] || '#e8c600';
    const highPlusRate = (intensityShare.high?.rate || 0) + (intensityShare.extreme?.rate || 0);
    const verb = INTENSITY_VERB[dominantIntensity];
    const familyLabel = topFamily ? prettyFamily(topFamily) : null;

    const blurbParts = [];
    if (familyLabel) blurbParts.push(`${familyLabel}-flavored`);
    if (verb) blurbParts.push(verb);
    const blurb = blurbParts.length > 0 ? blurbParts.join(' — ') : null;
    const tailBlurb =
        highPlusRate > 0 ? `${formatPct(highPlusRate)} of decks push into high or extreme territory.` : null;

    return (
        <div
            className="saltHeroCard"
            style={{
                background: `linear-gradient(135deg, ${hexToRgba(accent, 0.22)} 0%, rgba(10, 10, 10, 0.95) 55%)`,
                borderLeftColor: accent,
            }}
        >
            <div className="saltHeroGlowDot" style={{ background: accent, boxShadow: `0 0 24px ${accent}` }} />
            <div className="saltHeroEyebrow">Salt personality</div>
            <div className="saltHeroHeadlineRow">
                {dominantIntensity && (
                    <span
                        className="saltHeroBadge"
                        style={{
                            color: accent,
                            borderColor: accent,
                            boxShadow: `0 0 18px ${hexToRgba(accent, 0.55)}, inset 0 0 10px ${hexToRgba(accent, 0.18)}`,
                        }}
                    >
                        {INTENSITY_LABEL[dominantIntensity] || humanize(dominantIntensity)}
                    </span>
                )}
                {familyLabel && <span className="saltHeroFamily">{familyLabel}</span>}
            </div>
            {(blurb || tailBlurb) && (
                <div className="saltHeroBlurb">
                    {blurb && <span>{blurb}.</span>}
                    {tailBlurb && <span className="saltHeroBlurbTail">{` ${tailBlurb}`}</span>}
                </div>
            )}
            <div className="saltHeroIntensityStrip">
                {INTENSITY_ORDER.map((k) => {
                    const rate = intensityShare[k]?.rate || 0;
                    const count = intensityShare[k]?.count || 0;
                    if (rate <= 0) return null;
                    return (
                        <div
                            key={k}
                            className="saltHeroIntensitySegment"
                            style={{ width: `${rate * 100}%`, background: INTENSITY_COLOR[k] }}
                            title={`${INTENSITY_LABEL[k]} — ${count} deck${count === 1 ? '' : 's'} (${formatPct(
                                rate
                            )})`}
                        >
                            <span className="saltHeroIntensitySegmentLabel">{INTENSITY_LABEL[k]}</span>
                            <span className="saltHeroIntensitySegmentValue">{formatPct(rate)}</span>
                        </div>
                    );
                })}
            </div>
            <div className="saltHeroChipGroups">
                <HeroChipRow
                    label="Primary"
                    entries={personality?.primaryArchetypes}
                    deckCount={deckCount}
                    accent={accent}
                />
                <HeroChipRow
                    label="Secondary"
                    entries={personality?.secondaryArchetypes}
                    deckCount={deckCount}
                    accent={accent}
                />
                <HeroChipRow label="Flavors" entries={personality?.flavors} deckCount={deckCount} accent={accent} />
            </div>
        </div>
    );
}

function HeroChipRow({ label, entries, deckCount, accent }) {
    if (!entries || entries.length === 0) return null;
    // Cap at 6 so the hero stays glance-able on mobile; full list is
    // still accessible via the server response for future drill-ins.
    const visible = entries.slice(0, 6);
    return (
        <div className="saltHeroChipRow">
            <span className="saltHeroChipRowLabel">{label}</span>
            <div className="saltHeroChipRowItems">
                {visible.map((entry) => {
                    const rate = entry.rate || 0;
                    const pct = Math.round(rate * 100);
                    return (
                        <span
                            key={entry.key}
                            className="saltHeroChip"
                            style={{ borderLeftColor: accent }}
                            title={`${entry.count} of ${deckCount} decks (${formatPct(rate)})`}
                        >
                            <span className="saltHeroChipLabel">
                                {entry.label || prettyCategory(entry.key)}
                            </span>
                            <span className="saltHeroChipCount">{`${pct}%`}</span>
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Top-6 podium for the worst-offenders list. Big card thumb, rank
 * medal, and contribution stats stacked vertically. Ranks 1–3 take
 * gold/silver/bronze accents; ranks 4–6 share a muted seared-red
 * accent so the medal trio still reads as the standout tier.
 */
const PODIUM_ACCENTS = [
    { label: '#1', color: '#e8c600', glow: '0 0 28px rgba(232, 198, 0, 0.45)' },
    { label: '#2', color: '#c0c0c0', glow: '0 0 22px rgba(192, 192, 192, 0.35)' },
    { label: '#3', color: '#b87333', glow: '0 0 22px rgba(184, 115, 51, 0.35)' },
    { label: '#4', color: '#8a4a4a', glow: '0 0 14px rgba(138, 74, 74, 0.3)' },
    { label: '#5', color: '#7a4040', glow: '0 0 14px rgba(122, 64, 64, 0.28)' },
    { label: '#6', color: '#6a3838', glow: '0 0 14px rgba(106, 56, 56, 0.26)' },
];

function WorstOffenderPodium({ cards, deckCount, onSelectCard }) {
    if (!cards || cards.length === 0) return null;
    const top = cards.slice(0, 6);
    return (
        <div className="saltPodium">
            {top.map((card, idx) => {
                const accent = PODIUM_ACCENTS[idx];
                const name = card?.name || card?.cardId || 'Unknown';
                const imageUri = card?.imageUri;
                const cardId = card?.cardId || card?.id;
                const dominant = card?.dominantCategory;
                const dominantPct = Math.round((card?.dominantCategoryShare || 0) * 100);
                const handleClick = () => {
                    if (onSelectCard && cardId) {
                        onSelectCard({
                            id: cardId,
                            cardId,
                            name,
                            imageUri,
                            types: Array.isArray(card?.types) ? card.types.join(' ') : card?.types,
                        });
                    }
                };
                return (
                    <div
                        key={card.cardId}
                        className={`saltPodiumCard saltPodiumCard--rank${idx + 1}`}
                        style={{ borderColor: accent.color, boxShadow: accent.glow }}
                    >
                        <span className="saltPodiumRank" style={{ color: accent.color, borderColor: accent.color }}>
                            {accent.label}
                        </span>
                        {imageUri ? (
                            <img
                                src={imageUri}
                                alt={name}
                                className="saltPodiumThumb"
                                onClick={handleClick}
                                loading="lazy"
                            />
                        ) : (
                            <div className="saltPodiumThumb saltPodiumThumb--placeholder" />
                        )}
                        <span
                            className="saltPodiumName"
                            onClick={handleClick}
                            style={{ cursor: cardId ? 'pointer' : 'default' }}
                            title={cardId ? `Click to view ${name}` : name}
                        >
                            {name}
                        </span>
                        {dominant && (
                            <span
                                className="saltPodiumCategory"
                                style={{ borderLeftColor: categoryColor(dominant), color: categoryColor(dominant) }}
                                title={`${prettyCategory(
                                    dominant
                                )} drove ${dominantPct}% of this card's salt in the bin`}
                            >
                                {prettyCategory(dominant)}
                            </span>
                        )}
                        <div className="saltPodiumStats">
                            <div className="saltPodiumStat">
                                <span className="saltPodiumStatLabel">avg</span>
                                <span
                                    className="saltPodiumStatValue"
                                    style={{ color: accent.color }}
                                    title="Avg salt points this card contributed per deck that ran it"
                                >
                                    {`+${formatNumber(card?.avgContribution, 1)}`}
                                </span>
                            </div>
                            <div className="saltPodiumStat">
                                <span className="saltPodiumStatLabel">inclusion</span>
                                <span className="saltPodiumStatValue">
                                    {`${Math.round((card?.deckRate || 0) * 100)}%`}
                                </span>
                            </div>
                            <div className="saltPodiumStat">
                                <span className="saltPodiumStatLabel">decks</span>
                                <span className="saltPodiumStatValue">{`${card?.deckCount || 0}/${deckCount}`}</span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Very light combo-row adapter for the per-category topCombos list.
 */
function SaltComboRow({ combo, deckCount }) {
    const pct = Math.round((combo?.deckRate || 0) * 100);
    return (
        <div className="saltComboRow">
            <span className="saltComboName">{combo?.name || combo?.comboId}</span>
            <div className="saltComboBarTrack">
                <div className="saltComboBarFill" style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <span className="saltComboPct">{`${pct}%`}</span>
            <span className="saltComboCount">{`${combo?.deckCount || 0}/${deckCount}`}</span>
            <span className="saltComboAvg" title="Avg salt points contributed per deck running this combo">
                {`avg +${formatNumber(combo?.avgScore, 1)}`}
            </span>
        </div>
    );
}

/**
 * Offender row — CardStatRow variant that surfaces dominant salt
 * category and avg salt contribution instead of the generic count cell.
 */
function OffenderRow({ card, deckCount, onSelectCard }) {
    const inclusionRate = card?.deckRate || 0;
    const pct = Math.round(inclusionRate * 100);
    const name = card?.name || card?.cardId || 'Unknown';
    const imageUri = card?.imageUri;
    const cardId = card?.cardId || card?.id;
    const dominant = card?.dominantCategory;
    const dominantPct = Math.round((card?.dominantCategoryShare || 0) * 100);

    const handleClick = () => {
        if (onSelectCard && cardId) {
            onSelectCard({
                id: cardId,
                cardId,
                name,
                imageUri,
                types: Array.isArray(card?.types) ? card.types.join(' ') : card?.types,
            });
        }
    };

    return (
        <div className="saltOffenderRow">
            {imageUri ? (
                <img src={imageUri} alt={name} className="cardStatThumb" onClick={handleClick} loading="lazy" />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <span
                className="cardStatName"
                onClick={handleClick}
                style={{ cursor: cardId ? 'pointer' : 'default' }}
                title={cardId ? `Click to view ${name}` : name}
            >
                {name}
            </span>
            {dominant && (
                <span
                    className="saltOffenderCategoryChip"
                    style={{ borderLeftColor: categoryColor(dominant), color: categoryColor(dominant) }}
                    title={`${prettyCategory(
                        dominant
                    )} drove ${dominantPct}% of this card's salt across decks in this bucket`}
                >
                    {prettyCategory(dominant)}
                </span>
            )}
            <div className="cardStatBarTrack">
                <div className="cardStatBarFill" style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <span className="cardStatPercent">{`${pct}%`}</span>
            <span className="cardStatCount">{`${card?.deckCount || 0}/${deckCount}`}</span>
            <span
                className="saltOffenderContribution"
                title="Avg salt points this card contributed per deck that ran it"
            >
                {`avg +${formatNumber(card?.avgContribution, 1)}`}
            </span>
        </div>
    );
}

/**
 * Cheap hex → rgba converter for CSS gradient backdrops. Accepts a
 * `#RRGGBB` or `#RGB` string plus an alpha; returns `rgba(r,g,b,a)`.
 * We use this to tint the hero card with the intensity accent without
 * pulling in a color utility dep.
 */
function hexToRgba(hex, alpha) {
    if (!hex) return `rgba(232, 198, 0, ${alpha})`;
    let value = hex.replace('#', '');
    if (value.length === 3)
        value = value
            .split('')
            .map((c) => c + c)
            .join('');
    const int = parseInt(value, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function SaltTab({ bin, onSelectCard }) {
    const stats = bin?.saltStats;
    const [activeCategory, setActiveCategory] = useState(null);

    const byCategory = stats?.byCategory || [];
    const byCategoryMap = useMemo(() => {
        const map = new Map();
        byCategory.forEach((entry) => map.set(entry.category, entry));
        return map;
    }, [byCategory]);

    const activeCategoryEntry = activeCategory ? byCategoryMap.get(activeCategory) : null;

    if (!stats) {
        return (
            <Text UNSAFE_className="commanderCardStatsEmpty">
                Salt stats aren't available for this bucket yet — they'll populate after the next rebuild.
            </Text>
        );
    }

    const deckCount = stats.deckCount || bin?.deckCount || 0;
    const personality = stats.personality;
    const activeCategoryTopCards = (activeCategoryEntry?.topCards || []).map(toCardRowShape);
    const activeCategoryTopCombos = activeCategoryEntry?.topCombos || [];
    const topCards = stats.topCards || [];
    const topCardsForList = topCards.slice(6).map(toCardRowShape);

    return (
        <Flex direction="column" rowGap={24} marginTop={10}>
            <HeroPanel
                tone="purple"
                className="saltPersonality"
                eyebrow="Salt Profile"
                title="Salt signals at this level"
                hint="Common salt categories and how often decks fire them"
                aside={
                    <HeroPanelTotal
                        label="Decks"
                        value={deckCount.toLocaleString()}
                    />
                }
            />
            {personality && <SaltPersonalityHero personality={personality} deckCount={deckCount} />}

            <SaltPulseBanner stats={stats} deckCount={deckCount} />

            {byCategory.length > 0 && (
                <StatsSection
                    title="Why this commander is salty"
                    subtitle="Salt categories ranked by how often decks score in them. Click a row to drill into its top cards and combos."
                >
                    <div className="saltCategoryList">
                        {byCategory.map((entry) => {
                            const pct = Math.round((entry.deckRate || 0) * 100);
                            const isActive = activeCategory === entry.category;
                            const color = categoryColor(entry.category);
                            return (
                                <button
                                    key={entry.category}
                                    type="button"
                                    className={`saltCategoryRow ${isActive ? 'is-active' : ''}`}
                                    style={{ borderLeftColor: isActive ? color : 'transparent' }}
                                    onClick={() => setActiveCategory(isActive ? null : entry.category)}
                                    title={
                                        `${entry.deckCount} of ${deckCount} decks score in ${prettyCategory(
                                            entry.category
                                        )}.\n` +
                                        `Avg score when present: ${formatNumber(entry.avgScoreAmongSalted, 1)}.\n` +
                                        `Max per-deck: ${formatNumber(entry.maxScore, 1)}.`
                                    }
                                >
                                    <span className="saltCategoryName" style={{ color }}>
                                        {prettyCategory(entry.category)}
                                    </span>
                                    <div className="saltCategoryBarTrack">
                                        <div
                                            className="saltCategoryBarFill"
                                            style={{ width: `${Math.max(pct, 1)}%`, background: color }}
                                        />
                                    </div>
                                    <span className="saltCategoryPct">{`${pct}%`}</span>
                                    <span className="saltCategoryCount">{`${entry.deckCount}/${deckCount}`}</span>
                                    <span
                                        className="saltCategoryAvg"
                                        title="Average score per deck that scored in this category"
                                    >
                                        {`avg +${formatNumber(entry.avgScoreAmongSalted, 1)}`}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {activeCategoryEntry && (
                        <Flex direction="column" rowGap={8} marginTop={10}>
                            <FilterBanner
                                label="Showing"
                                value={prettyCategory(activeCategoryEntry.category)}
                                detail={`${activeCategoryEntry.deckCount}/${deckCount} decks · avg +${formatNumber(
                                    activeCategoryEntry.avgScoreAmongSalted,
                                    1
                                )} when present`}
                                color={categoryColor(activeCategoryEntry.category)}
                                onClear={() => setActiveCategory(null)}
                                ariaLabel="Clear salt category filter"
                            />
                            {activeCategoryTopCombos.length > 0 && (
                                <div className="saltComboList">
                                    <span className="saltCategorySubhead">Top combos</span>
                                    {activeCategoryTopCombos.map((combo) => (
                                        <SaltComboRow key={combo.comboId} combo={combo} deckCount={deckCount} />
                                    ))}
                                </div>
                            )}
                            <CardStatList
                                cards={activeCategoryTopCards}
                                deckCount={deckCount}
                                onSelectCard={onSelectCard}
                                resetKey={`salt:${activeCategory}:${bin?.binKey}`}
                                emptyMessageUnfiltered="No cards recorded for this category."
                            />
                        </Flex>
                    )}
                </StatsSection>
            )}

            {topCards.length > 0 && (
                <StatsSection
                    title="Worst offenders"
                    subtitle="Cards contributing the most salt across the bin, independent of category"
                >
                    <WorstOffenderPodium cards={topCards} deckCount={deckCount} onSelectCard={onSelectCard} />
                    {topCardsForList.length > 0 && (
                        <CardStatList
                            cards={topCardsForList}
                            deckCount={deckCount}
                            onSelectCard={onSelectCard}
                            resetKey={`saltOffenders:${bin?.binKey}`}
                            emptyMessageUnfiltered="No additional salt-scoring cards in this bucket."
                            renderCard={(card) => (
                                <OffenderRow
                                    key={card.cardId}
                                    card={card}
                                    deckCount={deckCount}
                                    onSelectCard={onSelectCard}
                                />
                            )}
                        />
                    )}
                </StatsSection>
            )}
        </Flex>
    );
}
